import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LobbyBanners = () => {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);

    const slides = [
        {
            id: 4,
            tags: [{ text: "¡EL CAMINO A LA GLORIA!", color: "bg-[#10B981] text-white" }],
            title1: "MUNDIAL",
            title2: "2026",
            title2Color: "text-[#FCD34D]", // Gold
            subtitle: "Acompaña a tu selección con las mejores cuotas.",
            cta: "JUGAR AHORA",
            image: "/assets/banners/banner_mundial.png",
            buttonColor: "bg-gradient-to-r from-[#10B981] to-[#059669] hover:brightness-110 shadow-[0_4px_14px_rgba(16,185,129,0.4)]",
            link: "/sports"
        },
        {
            id: 1,
            tags: [{ text: "¡VIVE LA EXPERIENCIA!", color: "bg-[#2563EB] text-white" }],
            title1: "CHAMPIONS",
            title2: "LEAGUE",
            title2Color: "text-[#60A5FA]", // Light Blue
            subtitle: "Las mejores cuotas para la UCL.",
            cta: "JUGAR AHORA",
            image: "/assets/banners/banner_ucl.png",
            buttonColor: "bg-[#2563EB] hover:bg-[#1d4ed8] shadow-[0_4px_14px_rgba(37,99,235,0.4)]",
            link: "/sports"
        },
        {
            id: 2,
            tags: [
                { text: "DEPÓSITO QR", color: "bg-[#10B981] text-white" },
                { text: "100% EXTRA", color: "bg-[#FCD34D] text-black" }
            ],
            title1: "DUPLICA TU",
            title2: "DINERO",
            title2Color: "text-[#10B981]", // Emerald
            subtitle: "DEPÓSITO QR 100% EXTRA",
            cta: "RECLAMAR",
            image: "/assets/banners/banner_bonus.png",
            buttonColor: "bg-[#10B981] hover:bg-[#059669] shadow-[0_4px_14px_rgba(16,185,129,0.4)]",
            link: "/deposit"
        },
        {
            id: 3,
            tags: [{ text: "EN VIVO", color: "bg-[#F97316] text-white", dot: true }],
            title1: "SIENTE LA",
            title2: "ADRENALINA",
            title2Color: "text-[#FBBF24]", // Amber
            subtitle: "Miles de mercados disponibles en tiempo real.",
            cta: "IR AL LIVE",
            image: "/assets/banners/banner_live.png",
            buttonColor: "bg-gradient-to-r from-[#F97316] to-[#F59E0B] hover:brightness-110 shadow-[0_4px_14px_rgba(249,115,22,0.4)]",
            link: "/sports"
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 6000);
        return () => clearInterval(timer);
    }, [slides.length]);

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

    return (
        <div className="px-4 mb-8 mt-2">
            <div className="relative overflow-hidden rounded-[2rem] h-52 md:h-[22rem] shadow-2xl group pb-8 bg-[#0a0a0a]">

                {/* Slides */}
                {slides.map((slide, index) => (
                    <div
                        key={slide.id}
                        onClick={() => navigate(slide.link || '/sports')}
                        className={`absolute inset-0 transition-opacity duration-700 ease-in-out cursor-pointer will-change-[opacity] ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                            }`}
                    >
                        {/* Background */}
                        <div className="absolute inset-0 overflow-hidden transform-gpu">
                            {/* 1. Blurred Background for Fill/Glow */}
                            <img
                                src={slide.image}
                                alt=""
                                loading={index === 0 ? "eager" : "lazy"}
                                className="absolute inset-0 w-full h-full object-cover blur-3xl scale-125 opacity-60"
                            />

                            {/* 2. Main Image */}
                            <img
                                src={slide.image}
                                alt={slide.title1}
                                loading={index === 0 ? "eager" : "lazy"}
                                fetchPriority={index === 0 ? "high" : "auto"}
                                className="relative w-full h-full object-cover z-0"
                            />

                            {/* 3. Dark Overlays for Contrast */}
                            <div className="absolute inset-0 bg-black/40 z-10"></div> {/* General Dimming */}
                            <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/60 to-transparent z-10"></div> {/* Text Gradient */}
                        </div>

                        {/* Content */}
                        <div className="relative h-full flex flex-col justify-center px-6 md:pl-28 md:pr-16 max-w-4xl z-20">

                            {/* Tags */}
                            <div className="flex items-center gap-2 mb-2 md:mb-6">
                                {slide.tags.map((tag, idx) => (
                                    <span key={idx} className={`inline-flex items-center gap-1.5 px-2.5 py-1 md:px-3.5 md:py-1.5 rounded-full text-[9px] md:text-[11px] font-black uppercase tracking-widest border border-white/10 ${tag.color}`}>
                                        {tag.dot && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>}
                                        {tag.text}
                                    </span>
                                ))}
                            </div>

                            {/* Title */}
                            <div className="flex flex-col mb-2 md:mb-4 leading-[0.85]">
                                <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white italic uppercase tracking-tighter drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
                                    {slide.title1}
                                </h2>
                                <h2 className={`text-3xl md:text-5xl lg:text-6xl font-black italic uppercase tracking-tighter drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)] ${slide.title2Color}`}>
                                    {slide.title2}
                                </h2>
                            </div>

                            <p className="text-neutral-200 text-xs md:text-lg font-medium mb-4 md:mb-8 tracking-wide drop-shadow-md max-w-sm md:max-w-xl">
                                {slide.subtitle}
                            </p>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(slide.link || '/sports');
                                }}
                                className={`${slide.buttonColor} text-white px-6 py-2.5 md:px-10 md:py-3.5 rounded-full font-black text-[9px] md:text-[12px] uppercase tracking-[0.2em] w-fit hover:scale-105 active:scale-95 transition-all outline-none shadow-lg`}
                            >
                                {slide.cta}
                            </button>
                        </div>
                    </div>
                ))}

                {/* Arrows */}
                < button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white/70 hover:text-white transition-all z-20 opacity-0 group-hover:opacity-100"
                >
                    <ChevronLeft size={20} strokeWidth={3} />
                </button>
                <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white/70 hover:text-white transition-all z-20 opacity-0 group-hover:opacity-100"
                >
                    <ChevronRight size={20} strokeWidth={3} />
                </button>

                {/* Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                    {slides.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentSlide(idx)}
                            className={`h-1.5 rounded-full transition-all duration-300 shadow-lg ${idx === currentSlide ? 'w-8 bg-[#F59E0B]' : 'w-1.5 bg-white/30 hover:bg-white/50'
                                }`}
                        />
                    ))}
                </div>
            </div>
        </div >
    );
};

export default LobbyBanners;
